import React, { useState, useEffect } from 'react';
import { MealItemWithHistory, OrphanMessage, adminAPI } from '../api';
import { formatTime, formatDisplayDate } from '../utils/dateUtils';

interface ConversationDetailProps {
  userId: number;
  date: string;
  onClose: () => void;
}

const ConversationDetail: React.FC<ConversationDetailProps> = ({
  userId,
  date,
  onClose,
}) => {
  const [items, setItems] = useState<MealItemWithHistory[]>([]);
  const [orphanMessages, setOrphanMessages] = useState<OrphanMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [userId, date]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await adminAPI.getUserDayConversations(userId, date);
      if (response.success) {
        setItems(response.items || []);
        setOrphanMessages(response.orphanMessages || []);
      } else {
        setError(response.error || 'Failed to load data');
      }
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-800 text-white">
            <div>
              <h2 className="text-xl font-semibold font-mono">
                User Input Debug View
              </h2>
              <p className="text-sm text-gray-300">
                {formatDisplayDate(date)} | User ID: {userId}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-300 hover:text-white text-2xl"
            >
              &times;
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
              </div>
            ) : error ? (
              <div className="text-center text-red-500 py-8">{error}</div>
            ) : items.length === 0 && orphanMessages.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                No data found for this day
              </div>
            ) : (
              <div className="space-y-4">
                {/* Items with message history */}
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white rounded-lg border border-gray-300 overflow-hidden shadow-sm"
                  >
                    {/* Item Header */}
                    <div className="bg-gray-100 px-4 py-3 border-b border-gray-300">
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="font-medium text-gray-800 text-lg">
                            {item.food_name}
                          </span>
                          {item.qty && item.unit && (
                            <span className="text-gray-500 text-sm ml-2">
                              ({item.qty} {item.unit})
                            </span>
                          )}
                        </div>
                        <span className="text-xs font-mono text-gray-400">
                          ID: {item.id}
                        </span>
                      </div>
                      <div className="flex space-x-3 mt-1 text-sm">
                        <span className="text-blue-600">
                          {Math.round(item.carbs)}g carbs
                        </span>
                        <span className="text-green-600">
                          {Math.round(item.protein)}g protein
                        </span>
                        <span className="text-amber-600">
                          {Math.round(item.fat)}g fat
                        </span>
                        <span className="text-purple-600 font-medium">
                          {Math.round(item.calories)} cal
                        </span>
                      </div>
                      {item.notes && (
                        <p className="text-sm text-gray-500 italic mt-1">
                          {item.notes}
                        </p>
                      )}
                    </div>

                    {/* Message History */}
                    <div className="p-4">
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                        Message History
                      </div>

                      {item.messageHistory.length === 0 ? (
                        <div className="text-gray-400 italic text-sm">
                          No message history recorded
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {item.messageHistory.map((msg, idx) => (
                            <div key={msg.id} className="flex items-start gap-3">
                              {/* Timeline connector */}
                              <div className="flex flex-col items-center">
                                <div className={`w-2 h-2 rounded-full ${msg.action === 'created' ? 'bg-green-500' : 'bg-blue-500'}`} />
                                {idx < item.messageHistory.length - 1 && (
                                  <div className="w-0.5 h-full bg-gray-300 mt-1" />
                                )}
                              </div>

                              {/* Message content */}
                              <div className="flex-1 pb-2">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`text-xs px-2 py-0.5 rounded ${msg.action === 'created' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                    {msg.action === 'created' ? 'Created' : 'Updated'}
                                  </span>
                                  <span className="text-xs text-gray-400">
                                    {formatTime(msg.timestamp)}
                                  </span>
                                </div>

                                {/* Image */}
                                {msg.imageUrl && (
                                  <div className="mb-2">
                                    <img
                                      src={msg.imageUrl}
                                      alt="User uploaded"
                                      className="max-w-xs max-h-32 rounded border cursor-pointer hover:opacity-80 transition-opacity"
                                      onClick={() => setExpandedImage(msg.imageUrl || null)}
                                    />
                                  </div>
                                )}

                                {/* Text */}
                                {msg.text ? (
                                  <div className="bg-gray-50 p-2 rounded border border-gray-200">
                                    <p className="text-gray-700 text-sm whitespace-pre-wrap">
                                      {msg.text}
                                    </p>
                                  </div>
                                ) : msg.imageUrl ? (
                                  <span className="text-gray-400 italic text-sm">(Image only)</span>
                                ) : null}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Orphan Messages (not linked to current items) */}
                {orphanMessages.length > 0 && (
                  <div className="mt-6">
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                      Unlinked Messages (possibly deleted items)
                    </div>
                    <div className="space-y-2">
                      {orphanMessages.map((msg) => (
                        <div key={msg.id} className="bg-gray-100 p-3 rounded border border-gray-300">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs text-gray-500">
                              {msg.action} items: {msg.itemIds.join(', ') || 'none'}
                            </span>
                            <span className="text-xs text-gray-400">
                              {formatTime(msg.timestamp)}
                            </span>
                          </div>
                          {msg.imageUrl && (
                            <div className="mb-2">
                              <img
                                src={msg.imageUrl}
                                alt="User uploaded"
                                className="max-w-xs max-h-32 rounded border cursor-pointer hover:opacity-80"
                                onClick={() => setExpandedImage(msg.imageUrl || null)}
                              />
                            </div>
                          )}
                          {msg.text && (
                            <p className="text-gray-600 text-sm">{msg.text}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-100">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Image Modal */}
      {expandedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] cursor-pointer"
          onClick={() => setExpandedImage(null)}
        >
          <img
            src={expandedImage}
            alt="Expanded view"
            className="max-w-[90vw] max-h-[90vh] object-contain"
          />
        </div>
      )}
    </>
  );
};

export default ConversationDetail;
